import * as React from 'react'
import { useEventListener } from '../effects/EventListener'
import { getBoundingRect } from '../utils/dom'
import { getAnchorPosition } from '../utils/getAnchorPosition'
import { Omit, Placement, PlacementNS } from '../utils/types'

/* TYPES */

interface AnchorConfig {
  canFlip?: boolean
  dependencies?: Array<any>
  onLayout?: (anchor: AnchorApi) => any
  padding?: number
  placement: Placement
  overlay: React.RefObject<HTMLElement>
  target: React.RefObject<HTMLElement>
}

export type AnchorElement =
  | React.ReactNode
  | ((props: AnchorApi) => React.ReactElement)

export interface AnchorProps extends AnchorConfig {
  children: AnchorElement
}

interface AnchorRenderProps extends Omit<AnchorConfig, 'overlay'> {
  children: AnchorElement
  overlay?: React.RefObject<HTMLElement>
}

export interface AnchorApi {
  left: number
  minWidth: number
  placement: Placement
  top: number
}

export const initAnchor: AnchorApi = {
  left: 0,
  minWidth: 0,
  placement: PlacementNS.bottomLeft,
  top: 0,
}

/* HOOK */

export const useAnchor: (config: AnchorConfig) => AnchorApi = ({
  canFlip = true,
  dependencies = [],
  onLayout = () => {},
  padding = 0,
  placement = PlacementNS.bottomLeft,
  overlay: overlay,
  target,
}) => {
  const [anchor, setAnchor] = React.useState<AnchorApi>({
    ...initAnchor,
    placement,
  })

  const viewChangeHandler = () => {
    if (overlay.current && target.current) {
      const anchor = getAnchorPosition(
        placement,
        padding,
        undefined,
        getBoundingRect(target.current),
        getBoundingRect(overlay.current),
        canFlip
      )
      setAnchor(anchor)
      onLayout(anchor)
    }
  }

  useEventListener({
    event: 'scroll',
    handler: viewChangeHandler,
    options: { capture: true },
  })

  useEventListener({
    event: 'resize',
    handler: viewChangeHandler,
  })

  React.useLayoutEffect(() => {
    viewChangeHandler()
  }, [overlay, ...dependencies])

  return anchor
}

/* DECORATOR */

export const withAnchor = <T extends {}>(config: AnchorConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { anchor: AnchorConfig }) => {
  return (
    <Component {...props} anchor={useAnchor({ ...config, ...props.anchor })} />
  )
}

/* RENDER PROP */

export const Anchor = React.forwardRef<HTMLElement, AnchorRenderProps>(
  ({ children, ...props }, forwardedRef) => {
    const overlay =
      props.overlay || (forwardedRef as React.RefObject<HTMLElement>)
    const anchor = useAnchor({ ...props, overlay })

    const element =
      typeof children === 'function'
        ? (children as (props: AnchorApi) => React.ReactElement)(anchor)
        : (children as React.ReactElement)

    return (
      element &&
      React.cloneElement(element, {
        ...element.props,
        placement: anchor.placement,
        ref: overlay,
      })
    )
  }
)

/* CONTEXT */

export const AnchorContext = React.createContext(initAnchor)

export const AnchorProvider = ({ children, ...props }: AnchorProps) => (
  <AnchorContext.Provider value={useAnchor(props)}>
    {children}
  </AnchorContext.Provider>
)

export const consumeAnchor = () => React.useContext(AnchorContext)
